import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, CalendarDays, MapPin, StickyNote, FileText, Hammer } from 'lucide-react';
import { statusLabel, statusColor } from '../../utils/job-status';

const INK   = '#2B2620';
const STONE = '#8C8275';
const LINE  = 'rgba(43, 38, 32, 0.09)';

const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const DOC_TYPES = [
  { key: 'cutlist',    label: 'Cut List' },
  { key: 'glassorder', label: 'Glass Order' },
  { key: 'flexscreen', label: 'FlexScreen' },
  { key: 'shoporder',  label: 'Shop Order' },
];

function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

function openPdf(url) {
  if (!url) return;
  if (isAndroid()) {
    window.open('https://docs.google.com/gview?url=' + encodeURIComponent(url) + '&embedded=false', '_blank');
  } else {
    const separator = url.includes('?') ? '&' : '?';
    window.open(url + separator + 'response-content-disposition=inline', '_blank');
  }
}

export default function ActiveJobCard({ job }) {
  const navigate = useNavigate();
  // pdfUrls comes straight off the job doc — savePDFUrlToJob writes the same
  // map onto both jobs/{id} and cutlistJobs/{id}, so no per-card fetch needed.
  const pdfUrls = job.pdfUrls || {};
  const availableDocs = DOC_TYPES.filter(d => pdfUrls[d.key]);
  const accent = statusColor(job.status);

  const getProductDetails = () => {
    const parts = [];
    if (job.windowCount && job.windowCount > 0) parts.push(`${job.windowCount} Window${job.windowCount !== 1 ? 's' : ''}`);
    if (job.doorCount && job.doorCount > 0) parts.push(`${job.doorCount} Door${job.doorCount !== 1 ? 's' : ''}`);
    if (parts.length === 0 && job.itemCount) parts.push(`${job.itemCount} Item${job.itemCount !== 1 ? 's' : ''}`);
    if (parts.length === 0) return 'See notes for details';
    return parts.join(' — ');
  };

  const getStartedInfo = () => {
    const startedBy = job.productionStartedBy || job.startedBy || 'Management';
    if (job.productionStartedAt) {
      const date = job.productionStartedAt.toDate ? job.productionStartedAt.toDate() : new Date(job.productionStartedAt);
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      return `Started by ${startedBy} on ${formattedDate}`;
    }
    return `Started by ${startedBy}`;
  };

  const getSoldDate = () => {
    if (job.soldDate) {
      const date = job.soldDate.toDate ? job.soldDate.toDate() : new Date(job.soldDate);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    if (job.createdAt) {
      const date = job.createdAt.toDate ? job.createdAt.toDate() : new Date(job.createdAt);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return 'Recent';
  };

  return (
    <div style={{
      background: '#fff', borderRadius: '16px', padding: '20px', marginBottom: '12px',
      boxShadow: '0 2px 8px rgba(43,38,32,0.06)', border: `1px solid ${LINE}`,
      position: 'relative', overflow: 'hidden', fontFamily: FONT_SANS
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: accent }} />

      <div onClick={() => navigate(`/shop/job/${job.id}`)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: INK, marginBottom: '4px' }}>
              #{job.contractNumber || job.id?.slice(0, 6)}
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: STONE, marginBottom: '8px' }}>
              {job.customerName || job.name || 'Customer'}
            </div>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
            background: accent + '18', borderRadius: '10px', border: `1px solid ${accent}33`,
            fontSize: '12px', fontWeight: '700', color: accent, whiteSpace: 'nowrap'
          }}>
            {statusLabel(job.status)}
          </div>
        </div>

        <div style={{
          padding: '14px', background: 'rgba(43,38,32,0.03)',
          borderRadius: '12px', border: `1px solid ${LINE}`, marginBottom: '12px'
        }}>
          <div style={{ fontSize: '11px', color: accent, fontWeight: '700', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Products</div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: INK, marginBottom: '8px' }}>{getProductDetails()}</div>
          {job.programDescription && (
            <div style={{ fontSize: '13px', color: STONE, fontWeight: '500', marginTop: '6px' }}>{job.programDescription}</div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: job.jobType ? 'repeat(2, 1fr)' : '1fr', gap: '12px', marginBottom: '12px' }}>
          {job.jobType && (
            <div style={{ padding: '12px', background: 'rgba(43,38,32,0.03)', borderRadius: '10px', border: `1px solid ${LINE}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: STONE, marginBottom: '4px', fontWeight: '600' }}>
                <ClipboardList size={12} strokeWidth={2.25} /> Job Type
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: INK }}>{job.jobType}</div>
            </div>
          )}
          <div style={{ padding: '12px', background: 'rgba(43,38,32,0.03)', borderRadius: '10px', border: `1px solid ${LINE}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: STONE, marginBottom: '4px', fontWeight: '600' }}>
              <CalendarDays size={12} strokeWidth={2.25} /> Sold Date
            </div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: INK }}>{getSoldDate()}</div>
          </div>
        </div>

        {(job.address || job.city || job.state) && (
          <div style={{
            padding: '12px', background: 'rgba(43,38,32,0.03)', borderRadius: '10px',
            border: `1px solid ${LINE}`, marginBottom: '12px'
          }}>
            <div style={{ fontSize: '11px', color: STONE, fontWeight: '600', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <MapPin size={12} strokeWidth={2.25} /> Location
            </div>
            <div style={{ fontSize: '13px', color: STONE, lineHeight: '1.4' }}>
              {job.address && <div style={{ fontWeight: '600', color: INK }}>{job.address}</div>}
              {(job.city || job.state) && <div>{job.city}{job.city && job.state ? ', ' : ''}{job.state}</div>}
            </div>
          </div>
        )}

        <div style={{
          padding: '12px', background: accent + '0d', borderRadius: '10px',
          border: `1px solid ${accent}33`, marginBottom: job.notes ? '12px' : '0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: accent, fontWeight: '600', marginBottom: '6px' }}>
            <Hammer size={12} strokeWidth={2.25} /> Production Status
          </div>
          <div style={{ fontSize: '13px', color: STONE, fontWeight: '500' }}>{getStartedInfo()}</div>
        </div>

        {job.notes && (
          <div style={{ padding: '12px', background: 'rgba(43,38,32,0.03)', borderRadius: '10px', border: `1px dashed ${LINE}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: STONE, fontWeight: '600', marginBottom: '6px' }}>
              <StickyNote size={12} strokeWidth={2.25} /> Notes
            </div>
            <div style={{ fontSize: '13px', color: STONE, lineHeight: '1.5' }}>{job.notes}</div>
          </div>
        )}
      </div>

      {availableDocs.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${LINE}` }}>
          {availableDocs.map(d => (
            <button key={d.key} onClick={(e) => { e.stopPropagation(); openPdf(pdfUrls[d.key]); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 14px', borderRadius: '10px', border: `1px solid ${LINE}`,
                background: 'rgba(43,38,32,0.03)', color: INK, fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', fontFamily: FONT_SANS
              }}>
              <FileText size={13} strokeWidth={2.25} /> {d.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
